import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box, ThemeProvider } from '@mui/material';

import { AdminRoles, AuthorizedRoles, DoctorRoles, PatientDataMode, PatientRoles, Role } from './config/DiactiveTypes';
import DiactiveTheme, { AppBgColor } from './config/DiactiveTheme';
import DrawerAppBar from './components/DrawerAppBar';
import ProtectedRoute from './components/ProtectedRoute';
import ContactFab from './components/common/ContactFab';
import { onAuthStateChange, getAuthUserRole, signOutUser } from './services/AuthService';
import { preloadHospitals } from './services/HospitalService';

import SignIn from './views/SignIn';
import Dashboard from './views/Dashboard';
import PatientDashboard from './views/PatientDashboard';
import PatientDetails from './views/PatientDetails';
import Tutorials from './views/Tutorials';
import SetPatientData from './views/SetPatientData';
import FillQuestionnaire from './views/FillQuestionnaire';
import EmbedQuestionnaire from './views/EmbedQuestionnaire';
import ViewQuestionnaire from './views/ViewQuestionnaire';
import ResetPassword from './views/ResetPassword';
import PendingMailSummary from './views/PendingMailSummary';
import IncidentsSummary from './views/IncidentsSummary';
import UsersSummary from './views/UsersSummary';
import UserDetails from './views/UserDetails';
import SetUserData from './views/SetUserData';
import HospitalsSummary from './views/HospitalsSummary';
import TutorialsAdmin from './views/TutorialsAdmin';
import HospitalDetails from './views/HospitalDetails';
import SetHospitalData from './views/SetHospitalData';
import Faq from './views/Faq';

const App = () => {
  const isEmbedRoute = window.location.pathname.startsWith('/embed-questionnaire');

  const [isAuth, setIsAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const finalizeAuthState = (nextIsAuth, nextRole) => {
      setIsAuth(nextIsAuth);
      setRole(nextRole);
      setTimeout(() => setLoading(false), 1000);
    };

    const unsubscribe = onAuthStateChange(async (user) => {
      if (!user) {
        finalizeAuthState(false, null);
        return;
      }

      const authUserRole = await getAuthUserRole();
      const isAuthorized = AuthorizedRoles.includes(authUserRole);
      if (!isAuthorized) {
        await signOutUser();
        finalizeAuthState(false, null);
        return;
      }

      // Prime the hospitals cache so synchronous consumers (getHospitalName,
      // table filter options, form selects, etc.) have data on first render.
      try {
        await preloadHospitals();
      } catch (error) {
        console.error("Error preloading hospitals:", error);
      }

      finalizeAuthState(true, authUserRole);
    });

    if (isEmbedRoute) document.body.style.backgroundColor = AppBgColor;

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={DiactiveTheme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress size={45} sx={{ color: isEmbedRoute ? '#fff' : 'default' }} />
        </Box>
      </ThemeProvider>
    );
  }

  const isAdminOrDoctorRole = AdminRoles.includes(role) || DoctorRoles.includes(role);

  return (
    <ThemeProvider theme={DiactiveTheme}>
      <BrowserRouter>
        <DrawerAppBar isAuth={isAuth} role={role} />
        {isAuth && <ContactFab />}
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
          <Routes>
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {!isAuth && (
              <>
                <Route path="/sign-in/:reset?" element={<SignIn />} />
                <Route path="/embed-questionnaire/:token" element={<EmbedQuestionnaire />} />
                <Route path="*" element={<Navigate to="/sign-in" />} />
              </>
            )}
            {isAdminOrDoctorRole && (
                <>
                  <Route path="/" element={<ProtectedRoute isAuth={isAuth} role={role} component={Dashboard} />} />
                  <Route path="/patient/view/:patientId/:tabId?" element={<ProtectedRoute isAuth={isAuth} role={role} component={PatientDetails} />} />
                </>
            )}
            {DoctorRoles.includes(role) && (
              <>
                <Route path="/tutorials" element={<ProtectedRoute isAuth={isAuth} role={role} component={Tutorials} />} />
                <Route path="/faqs/" element={<ProtectedRoute isAuth={isAuth} role={role} component={Faq} />} />
                <Route
                  path="/patient/eval/:phase/:patientId?"
                  element={
                    <ProtectedRoute
                      isAuth={isAuth}
                      role={role}
                      component={props => <SetPatientData {...props} mode={PatientDataMode.Eval} />}
                    />
                  }
                />
              </>
            )}
            {AdminRoles.includes(role) && (
              <>
                <Route path="/pending-mail/" element={<ProtectedRoute isAuth={isAuth} role={role} component={PendingMailSummary} />} />
                <Route path="/incidents/" element={<ProtectedRoute isAuth={isAuth} role={role} component={IncidentsSummary} />} />
                {userTypeRoutes({ userType: Role.Doctor, plural: 'doctors', singular: 'doctor', isAuth, role })}
                {role === Role.Admin && userTypeRoutes({ userType: Role.SuperUser, plural: 'admins', singular: 'admin', isAuth, role })}
                {userTypeRoutes({ userType: Role.AppTester, plural: 'app-testers', singular: 'app-tester', isAuth, role })}
                <Route path="/admin/faqs/patients/" element={<ProtectedRoute isAuth={isAuth} role={role} component={props => <Faq {...props} faqType="patient" />} />} />
                <Route path="/admin/faqs/doctors/" element={<ProtectedRoute isAuth={isAuth} role={role} component={props => <Faq {...props} faqType="doctor" />} />} />
                <Route path="/hospitals/" element={<ProtectedRoute isAuth={isAuth} role={role} component={HospitalsSummary} />} />
                <Route path="/admin/tutorials/" element={<ProtectedRoute isAuth={isAuth} role={role} component={TutorialsAdmin} />} />
                <Route path="/hospital/add" element={<ProtectedRoute isAuth={isAuth} role={role} component={SetHospitalData} />} />
                <Route path="/hospital/edit/:hospitalId" element={<ProtectedRoute isAuth={isAuth} role={role} component={SetHospitalData} />} />
                <Route path="/hospital/view/:hospitalId" element={<ProtectedRoute isAuth={isAuth} role={role} component={HospitalDetails} />} />
                <Route path="/questionnaire/:phase/:questionnaireId/:patientId" element={<ProtectedRoute isAuth={isAuth} role={role} component={ViewQuestionnaire} />} />
                <Route
                  path="/patient/edit/:phase/:patientId"
                  element={
                    <ProtectedRoute
                      isAuth={isAuth}
                      role={role}
                      component={props => <SetPatientData {...props} mode={PatientDataMode.Edit} />}
                    />
                  }
                />
              </>
            )}
            {PatientRoles.includes(role) && (
              <>
                <Route path="/" element={<ProtectedRoute isAuth={isAuth} role={role} component={PatientDashboard} />} />
                <Route path="/questionnaire/:phase/:questionnaireId" element={<ProtectedRoute isAuth={isAuth} role={role} component={FillQuestionnaire} />} />
                <Route path="/tutorials" element={<ProtectedRoute isAuth={isAuth} role={role} component={Tutorials} />} />
                <Route path="/faqs/" element={<ProtectedRoute isAuth={isAuth} role={role} component={Faq} />} />
              </>
            )}
            <Route path="/*" element={<Navigate to="/" />} />
          </Routes>
        </Box>
      </BrowserRouter>
    </ThemeProvider>
  );
};

const userTypeRoutes = ({ userType, plural, singular, isAuth, role }) => (
  <>
    <Route
      path={`/${plural}/`}
      element={<ProtectedRoute isAuth={isAuth} role={role} component={(props) => <UsersSummary {...props} userType={userType} />} />}
    />
    <Route
      path={`/${singular}/add`}
      element={<ProtectedRoute isAuth={isAuth} role={role} component={(props) => <SetUserData {...props} userType={userType} />} />}
    />
    <Route
      path={`/${singular}/edit/:userId`}
      element={<ProtectedRoute isAuth={isAuth} role={role} component={(props) => <SetUserData {...props} userType={userType} />} />}
    />
    <Route
      path={`/${singular}/view/:userId/:tabId?`}
      element={<ProtectedRoute isAuth={isAuth} role={role} component={(props) => <UserDetails {...props} userType={userType} />} />}
    />
  </>
);

export default App;